import React from 'react';
import { ToolCallResult } from '../types/assistant';
import { ToolCallInfo } from '../types/ai';
import { AVAILABLE_TOOLS } from '../types/tools';
import { ChevronDown, ChevronUp, Loader2, Copy } from 'lucide-react';
import { safeStringify } from '../utils/sanitize-display';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
      title={copied ? 'Copiado!' : 'Copiar'}
    >
      <Copy className="w-3 h-3" />
      {copied ? <span className="text-green-600">Copiado!</span> : <span>Copiar</span>}
    </button>
  );
}

interface ToolCallDisplayProps {
  toolCall: ToolCallResult | ToolCallInfo;
}

type ToolDetailTab = 'request' | 'response' | 'calculos';

export function ToolCallDisplay({ toolCall }: ToolCallDisplayProps) {
  const [expanded, setExpanded] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<ToolDetailTab>('request');
  
  const tool = AVAILABLE_TOOLS.find(t => t.id === toolCall.toolId);
  const operation = tool?.operations.find(op => op.id === toolCall.operationId);

  // Gerar resumo do processo
  const getSummary = () => {
    if (toolCall.status === 'running' || toolCall.status === 'pending') {
      return 'Processando';
    }
    if (toolCall.status === 'failed') {
      return toolCall.error || 'Erro na execução';
    }
    if (toolCall.status === 'completed' && toolCall.result) {
      // Para get_sale_items com response + calculos
      if (toolCall.result && typeof toolCall.result === 'object' && 'response' in toolCall.result && Array.isArray((toolCall.result as any).response)) {
        const n = (toolCall.result as any).response.length;
        return n === 0 ? 'Nenhum resultado' : `${n} item(ns) + cálculos`;
      }
      // Para list_events - mostrar quantidade de resultados
      if (Array.isArray(toolCall.result)) {
        if (toolCall.result.length === 0) {
          return 'Nenhum resultado encontrado';
        }
        return `${toolCall.result.length} resultado(s)`;
      }
      // Para eventos do calendário com items
      if (toolCall.result.items && Array.isArray(toolCall.result.items)) {
        return `${toolCall.result.items.length} resultado(s)`;
      }
      // Para create_event - mostrar título do evento
      if (toolCall.result.summary) {
        return `Evento: ${toolCall.result.summary}`;
      }
      // Para web_search - mostrar query
      if (toolCall.result.query) {
        return toolCall.result.query;
      }
      // Para arquivos gerados
      if (toolCall.result.filename) {
        return toolCall.result.filename;
      }
      if (toolCall.result === true) {
        return 'Concluído com sucesso';
      }
    }
    return 'Concluído';
  };

  const isRunning = toolCall.status === 'running' || toolCall.status === 'pending';

  return (
    <div className="text-xs rounded-lg transition-colors text-gray-500">
      <div 
        className="flex items-center gap-2 cursor-pointer transition-colors hover:text-gray-700"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Logo alinhada à linha do texto */}
        {tool && (
          tool.icon.startsWith('/') || tool.icon.startsWith('http') ? (
            <span className="inline-flex items-center justify-center shrink-0 w-4 h-4 leading-none">
              <img src={tool.icon} alt={tool.name} className="w-4 h-4 object-contain align-middle" />
            </span>
          ) : (
            <span className="text-sm opacity-70 leading-none">{tool.icon}</span>
          )
        )}
        {!tool && (
          <span className="text-sm opacity-70 leading-none">🔧</span>
        )}
        
        {/* Nome da ferramenta e resumo */}
        <span className="leading-snug">
          <span className="font-medium">{operation?.name || toolCall.operationName || toolCall.operationId}</span>
          <span className="mx-1">·</span>
          <span className={isRunning ? 'animate-pulse' : ''}>{getSummary()}</span>
        </span>
        
        {/* Durante execução: mesmo layout, com "Processando" ao lado */}
        {isRunning ? (
          <span className="flex-shrink-0 flex items-center gap-1 text-gray-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          </span>
        ) : (
          expanded ? (
            <ChevronUp className="w-3 h-3 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-3 h-3 flex-shrink-0" />
          )
        )}
      </div>

      {/* Detalhamento com abas: Request | Response | Cálculos */}
      {expanded && (
        <div className="mt-1.5 ml-5">
          {(() => {
            const hasCalculos = toolCall.status === 'completed' && toolCall.result && typeof toolCall.result === 'object' && 'calculos' in toolCall.result;
            const requestContent = safeStringify({
              operation: toolCall.operationId || toolCall.operationName || 'unknown',
              parameters: toolCall.parameters != null && Object.keys(toolCall.parameters).length > 0
                ? toolCall.parameters
                : {}
            });
            const hasResponseAndCalculos = toolCall.result && typeof toolCall.result === 'object' && 'response' in toolCall.result && 'calculos' in toolCall.result;
            const responseContent = toolCall.status === 'completed' && toolCall.result !== undefined
              ? (hasResponseAndCalculos ? safeStringify((toolCall.result as any).response) : safeStringify(toolCall.result))
              : '';
            const calculosContent = hasCalculos ? String((toolCall.result as any).calculos) : '';
            const tabs: { id: ToolDetailTab; label: string }[] = [
              { id: 'request', label: 'Request' },
              { id: 'response', label: 'Response' },
              ...(hasCalculos ? [{ id: 'calculos' as const, label: 'Cálculos' }] : [])
            ];

            return (
              <>
                {/* Abas */}
                <div className="flex gap-0.5 border-b border-gray-200 mb-0">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-2.5 py-1.5 text-xs font-medium rounded-t transition-colors ${
                        activeTab === tab.id
                          ? 'bg-gray-100 text-gray-800 border border-gray-200 border-b-0 -mb-px'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Conteúdo da aba ativa */}
                <div className="rounded-b border border-gray-200 border-t-0 overflow-hidden">
                  {activeTab === 'request' && (
                    <>
                      <div className="px-2 py-1 bg-gray-100 text-gray-600 font-medium text-xs flex items-center justify-between border-b border-gray-200">
                        <span>Request</span>
                        <CopyButton text={requestContent} />
                      </div>
                      <div className="p-2 bg-gray-50 text-xs font-mono overflow-x-auto max-h-40">
                        <pre className="text-gray-600 whitespace-pre-wrap break-words">{requestContent}</pre>
                      </div>
                    </>
                  )}
                  {activeTab === 'response' && (
                    toolCall.status === 'completed' ? (
                      <>
                        <div className="px-2 py-1 bg-gray-100 text-gray-600 font-medium text-xs flex items-center justify-between border-b border-gray-200">
                          <span>Response</span>
                          <span className="flex items-center gap-1">
                            {toolCall.httpStatus != null && <span className="font-mono text-gray-700">HTTP {toolCall.httpStatus}</span>}
                            <CopyButton text={responseContent} />
                          </span>
                        </div>
                        <div className="p-2 bg-gray-50 text-xs font-mono overflow-x-auto max-h-40">
                          <pre className="text-gray-600 whitespace-pre-wrap break-words">{responseContent}</pre>
                        </div>
                      </>
                    ) : toolCall.status === 'failed' ? (
                      <>
                        <div className="px-2 py-1 bg-red-50 text-red-700 font-medium text-xs flex items-center justify-between border-b border-red-200">
                          <span>Response (erro)</span>
                          <span className="flex items-center gap-1">
                            {toolCall.httpStatus != null && <span className="font-mono">HTTP {toolCall.httpStatus}</span>}
                            <CopyButton text={toolCall.error ?? 'Erro desconhecido'} />
                          </span>
                        </div>
                        <div className="p-2 text-red-600 text-xs font-mono">
                          <pre className="whitespace-pre-wrap break-words">{toolCall.error ?? 'Erro desconhecido'}</pre>
                        </div>
                      </>
                    ) : null
                  )}
                  {activeTab === 'calculos' && hasCalculos && (
                    <>
                      <div className="px-2 py-1 bg-gray-100 text-gray-600 font-medium text-xs flex items-center justify-between border-b border-gray-200">
                        <span>Cálculos</span>
                        <CopyButton text={calculosContent} />
                      </div>
                      <div className="p-2 bg-gray-50 text-xs font-mono overflow-x-auto max-h-40">
                        <pre className="text-gray-600 whitespace-pre-wrap break-words">{calculosContent}</pre>
                      </div>
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// Componente para múltiplas chamadas de tools
interface ToolCallsListProps {
  toolCalls: (ToolCallResult | ToolCallInfo)[];
}

export function ToolCallsList({ toolCalls }: ToolCallsListProps) {
  if (!toolCalls || toolCalls.length === 0) return null;

  return (
    <div className="flex flex-col gap-1 mb-3">
      {toolCalls.map((toolCall, index) => (
        <ToolCallDisplay key={`${toolCall.toolId}-${index}`} toolCall={toolCall} />
      ))}
    </div>
  );
}

